import { motion, useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import type { MenuItem } from './types'

interface StartersSectionProps {
  id?: string
  title: string
  description?: string
  items: MenuItem[]
}

export function StartersSection({
  id,
  title,
  description,
  items,
}: StartersSectionProps) {
  const shouldReduceMotion = useReducedMotion()

  const baseTransition = { duration: 0.6, ease: 'easeOut' as const }
  const fadeUp = shouldReduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-80px' },
        transition: baseTransition,
      }

  const [featured, ...rest] = items

  return (
    <section
      id={id}
      className="bg-paper px-4 py-[clamp(4.5rem,10vw,8rem)] sm:px-6 lg:px-8"
      aria-labelledby={`${id}-heading`}
    >
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <motion.div {...fadeUp} className="mb-12 text-center sm:mb-16">
          <h2
            id={`${id}-heading`}
            className="font-display text-[clamp(2.5rem,7vw,4.5rem)] font-extrabold leading-[0.95] tracking-tighter text-ink"
          >
            {title}
          </h2>
          {description && (
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted">{description}</p>
          )}
        </motion.div>

        {/* Bento grid */}
        <motion.div
          {...fadeUp}
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2"
        >
          {/* Featured starter */}
          {featured && (
            <article className="group relative overflow-hidden rounded-2xl bg-white shadow-sm transition-all duration-300 hover:shadow-lg sm:col-span-2 lg:col-span-1 lg:row-span-2">
              <div className="relative aspect-[3/4] w-full overflow-hidden lg:aspect-auto lg:h-full">
                {featured.image ? (
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-full w-full bg-silver" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6 text-white">
                  <h3 className="font-display text-2xl font-semibold leading-tight sm:text-3xl">
                    {featured.title}
                  </h3>
                  {featured.description && (
                    <p className="mt-2 max-w-md text-sm leading-relaxed text-white/90 sm:text-base">
                      {featured.description}
                    </p>
                  )}
                  {featured.price && (
                    <p className="mt-3 text-base font-bold text-white">{featured.price}</p>
                  )}
                </div>
              </div>
            </article>
          )}

          {/* Remaining starters */}
          {rest.map((item, index) => (
            <article
              key={item.id ?? item.title}
              className={cn(
                'group overflow-hidden rounded-2xl bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg',
                index === rest.length - 1 && 'sm:col-span-2 lg:col-span-1'
              )}
            >
              <div className="relative aspect-[3/4] w-full overflow-hidden">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-full w-full bg-silver" />
                )}
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl font-semibold leading-tight text-ink">
                  {item.title}
                </h3>
                {item.description && (
                  <p className="mt-1 text-sm leading-relaxed text-muted">{item.description}</p>
                )}
                {item.price && (
                  <p className="mt-2 text-sm font-bold text-ink">{item.price}</p>
                )}
              </div>
            </article>
          ))}
        </motion.div>
      </div>
    </section>
  )
}
